import { ComponentProps, FC, useEffect, useState } from 'react';
import { CompanyInfo, ContractFormData } from '../../../../../types/contracts';
import {
  useGetCompaniesWithoutLoadingQuery,
  useGetCompanyQuery,
} from '../../../../../api/companyApi';
import Input from '../../../../../components/common/Input';
import { Company } from '../../../../../types/companies';
import FormItem from '../../../../../components/common/FormItem';
import { useFormContext } from 'react-hook-form';
import SuggestInput from '../../../../../components/common/SuggestInput';
import { halfWidthPhoneNumber, max, selected } from '../../../../../message';
import styles from './styles.module.css';
import { validateHalfWidthPhoneNumber } from '../../../../../validators/halfWidth';
import { ErrorMessage } from '@hookform/error-message';
import Caption, {
  CaptionsUnderForm,
} from '../../../../../components/common/Caption';

type CompaniesFormNamePrefix = keyof Pick<
  ContractFormData,
  'supplierCompanyInfo' | 'contractorCompanyInfo' | 'wastePointCompanyInfo'
>;
type Props = {
  namePrefix: CompaniesFormNamePrefix;
  entireDisabled?: boolean;
};
const initCompanies: Company[] = [];
type InputType = ComponentProps<typeof Input>;
const labels: Record<keyof CompanyInfo, string> = {
  companyId: '会社名',
  companyPicName: '担当者',
  phoneNumber: '担当者連絡先',
};
const setValueOption = {
  shouldValidate: true,
  shouldDirty: true,
  shouldTouched: true,
};
type CompanyDetailRecord = Record<
  | 'phoneNumber'
  | 'postCode'
  | 'prefecture'
  | 'address1'
  | 'address2'
  | 'address3',
  string
>;
const initCompanyDetailRecord: CompanyDetailRecord = {
  phoneNumber: '',
  postCode: '',
  prefecture: '',
  address1: '',
  address2: '',
  address3: '',
};

const CompanyInfoForm: FC<Props> = ({ namePrefix }) => {
  const names = {
    companyId: `${namePrefix}.companyId` as const,
    companyPicName: `${namePrefix}.companyPicName` as const,
    phoneNumber: `${namePrefix}.phoneNumber` as const,
  };
  const form = useFormContext<ContractFormData>();
  const { register, setValue, getValues, getFieldState, watch } = form;
  const [companyName, setCompanyName] = useState('');
  const [companyDetail, setCompanyDetail] = useState<CompanyDetailRecord>(
    initCompanyDetailRecord
  );
  const {
    data: { companies } = {
      companies: initCompanies,
    },
  } = useGetCompaniesWithoutLoadingQuery(
    {
      companyName,
      companyTypeId: 2 /** 顧客 */,
    },
    { skip: companyName.length > 255 }
  );
  register(names.companyId, {
    validate: {
      required: (v) =>
        (namePrefix === 'contractorCompanyInfo'
          ? !getValues('contractorCompanyInfo')
          : false) ||
        (namePrefix === 'wastePointCompanyInfo'
          ? getValues('wastePointCompanyIsTheSameSupplier')
          : false) ||
        v !== null ||
        selected(labels.companyId),
    },
  });
  const getCompaniesKey = (item: Company) => item.companyId;
  const getCompaniesLabel = (item: Company) => item.companyName;
  const doesShowCompanyDetail = companyDetail.phoneNumber !== '';
  const onClickSuggest = (company: Company) => {
    const current = getValues(namePrefix);
    if (!current) {
      return;
    }

    setCompanyName(company.companyName);
    setCompanyDetail({
      phoneNumber: company.phoneNumber,
      postCode: company.postCode,
      prefecture: company.prefecture.name,
      address1: company.address1,
      address2: company.address2,
      address3: company.address3 ?? '',
    });
    setValue(
      namePrefix,
      {
        ...current,
        companyId: company.companyId,
      },
      setValueOption
    );
  };
  const companyId = watch(names.companyId);
  const { data: company } = useGetCompanyQuery(companyId ?? 0, {
    skip: companyId === null,
  });
  useEffect(() => {
    if (company) {
      onClickSuggest(company);
    }
  }, [company]);
  const onChangeCompanyName = (companyName: string) => {
    setCompanyName(companyName);
    const current = getValues(namePrefix);
    if (!current) {
      return;
    }

    const company = companies.find((c) => c.companyName === companyName);
    setValue(
      namePrefix,
      {
        ...current,
        companyId: company?.companyId ?? null,
      },
      setValueOption
    );
    setCompanyDetail(initCompanyDetailRecord);
  };
  const companyIdVariants: InputType['variants'] = getFieldState(
    names.companyId
  ).error
    ? ['error', 'fit']
    : ['fit'];
  const companyPicNameVariants: InputType['variants'] = ['fit'];
  const phoneNumberVariants: InputType['variants'] = getFieldState(
    names.phoneNumber
  ).error
    ? ['error', 'fit']
    : ['fit'];

  return (
    <div>
      <div className={styles.line}>
        <FormItem label={labels.companyId}>
          <div className={styles.companyName}>
            <SuggestInput
              list={companies}
              value={companyName}
              getKey={getCompaniesKey}
              getLabel={getCompaniesLabel}
              onClickSuggest={onClickSuggest}
              onChange={onChangeCompanyName}
              variants={companyIdVariants}
            />
          </div>
        </FormItem>
        <FormItem label={labels.companyPicName} optional>
          <div className={styles.companyPicName}>
            <Input
              {...register(names.companyPicName, {
                validate: {
                  max: (value) =>
                    !value ||
                    value.length <= 255 ||
                    max(labels.companyPicName, 255),
                },
              })}
              variants={companyPicNameVariants}
            />
          </div>
        </FormItem>
        <FormItem label={labels.phoneNumber} optional>
          <div className={styles.phoneNumber}>
            <Input
              {...register(names.phoneNumber, {
                validate: {
                  max: (value) =>
                    !value ||
                    value.length <= 255 ||
                    max(labels.phoneNumber, 255),
                  halfWidthPhoneNumber: (value) =>
                    !value ||
                    validateHalfWidthPhoneNumber(value) ||
                    halfWidthPhoneNumber(labels.phoneNumber),
                },
              })}
              variants={phoneNumberVariants}
            />
          </div>
        </FormItem>
      </div>
      {doesShowCompanyDetail && (
        <div className={styles.companyDetail}>
          <div>{`電話番号: ${companyDetail.phoneNumber}`}</div>
          <div>{`郵便番号: ${companyDetail.postCode}`}</div>
          <div>{`都道府県: ${companyDetail.prefecture}`}</div>
          <div>{`市区町村: ${companyDetail.address1}`}</div>
          <div>{`丁目・番地: ${companyDetail.address2}`}</div>
          <div>{`建物名・部屋番号: ${companyDetail.address3}`}</div>
        </div>
      )}
      <CaptionsUnderForm>
        {Object.values(names).map((name) => (
          <ErrorMessage
            key={name}
            name={name}
            render={({ message }) => (
              <Caption variants={['error']}>{message}</Caption>
            )}
          />
        ))}
      </CaptionsUnderForm>
    </div>
  );
};

export default CompanyInfoForm;
